import React, { useState, useEffect } from 'react';

/**
 * OYONO - The Onerik Support Robot
 * A cute TV-headed robot with animated eyes and expressions
 */

const OyonoRobot = ({ 
  size = 'medium', 
  mood = 'happy', 
  speaking = false,
  className = '' 
}) => {
  const [blinking, setBlinking] = useState(false);
  const [eyePosition, setEyePosition] = useState({ x: 0, y: 0 });
  
  // Blink animation
  useEffect(() => {
    const blinkInterval = setInterval(() => {
      setBlinking(true);
      setTimeout(() => setBlinking(false), 150);
    }, 3000 + Math.random() * 2000);
    
    return () => clearInterval(blinkInterval);
  }, []);
  
  // Eye tracking effect (subtle movement)
  useEffect(() => {
    const moveEyes = () => {
      setEyePosition({
        x: (Math.random() - 0.5) * 4,
        y: (Math.random() - 0.5) * 3
      });
    };
    
    const eyeInterval = setInterval(moveEyes, 2000);
    return () => clearInterval(eyeInterval);
  }, []);
  
  const sizeMap = {
    small: { width: 60, height: 80 },
    medium: { width: 100, height: 130 },
    large: { width: 150, height: 195 }
  };
  
  const { width, height } = sizeMap[size] || sizeMap.medium;
  
  // Eye expressions based on mood
  const getEyeStyle = () => {
    if (blinking) return { scaleY: 0.1, borderRadius: '50%' };
    
    switch (mood) {
      case 'thinking':
        return { scaleY: 0.7, translateY: -2 };
      case 'excited':
        return { scaleY: 1.2, scaleX: 1.1 };
      case 'confused':
        return { rotate: '5deg' };
      default:
        return {};
    }
  };
  
  return (
    <div className={`relative ${className}`} style={{ width, height }}>
      <svg
        viewBox="0 0 100 130"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full"
      >
        {/* Antenna */}
        <g className="animate-bounce" style={{ animationDuration: '2s' }}>
          <line x1="50" y1="8" x2="50" y2="0" stroke="#4ECDC4" strokeWidth="2" strokeLinecap="round" />
          <circle cx="50" cy="4" r="4" fill="#FF6B6B" className="animate-pulse" />
        </g>
        
        {/* TV Head */}
        <rect
          x="15"
          y="8"
          width="70"
          height="55"
          rx="8"
          fill="url(#tvGradient)"
          stroke="#2D3748"
          strokeWidth="3"
        />
        
        {/* TV Screen (Face area) */}
        <rect
          x="22"
          y="15"
          width="56"
          height="41"
          rx="4"
          fill="#0a0a1a"
          className="transition-all duration-300"
        />
        
        {/* Screen glow effect */}
        <rect
          x="22"
          y="15"
          width="56"
          height="41"
          rx="4"
          fill="url(#screenGlow)"
          opacity="0.3"
        />
        
        {/* Scanlines effect */}
        <defs>
          <pattern id="scanlines" patternUnits="userSpaceOnUse" width="100" height="2">
            <line x1="0" y1="0" x2="100" y2="0" stroke="rgba(255,255,255,0.03)" strokeWidth="1" />
          </pattern>
        </defs>
        <rect x="22" y="15" width="56" height="41" rx="4" fill="url(#scanlines)" />
        
        {/* Eyes Container */}
        <g 
          style={{ 
            transform: `translate(${eyePosition.x}px, ${eyePosition.y}px)`,
            transition: 'transform 0.3s ease-out'
          }}
        >
          {/* Left Eye */}
          <ellipse
            cx="38"
            cy="32"
            rx="8"
            ry={blinking ? 1 : 10}
            fill="#4ECDC4"
            className="transition-all duration-100"
            style={getEyeStyle()}
          />
          <ellipse
            cx="39"
            cy="30"
            rx="3"
            ry={blinking ? 0.5 : 4}
            fill="#ffffff"
            opacity="0.8"
          />
          
          {/* Right Eye */}
          <ellipse
            cx="62"
            cy="32"
            rx="8"
            ry={blinking ? 1 : 10}
            fill="#4ECDC4"
            className="transition-all duration-100"
            style={getEyeStyle()}
          />
          <ellipse
            cx="63"
            cy="30"
            rx="3"
            ry={blinking ? 0.5 : 4}
            fill="#ffffff"
            opacity="0.8"
          />
        </g>
        
        {/* Mouth - changes with mood/speaking */}
        <g>
          {speaking ? (
            // Speaking animation - oval mouth
            <ellipse
              cx="50"
              cy="48"
              rx="8"
              ry="5"
              fill="#FF6B6B"
              className="animate-pulse"
              style={{ animationDuration: '0.3s' }}
            />
          ) : mood === 'happy' ? (
            // Happy smile
            <path
              d="M 40 46 Q 50 54 60 46"
              stroke="#4ECDC4"
              strokeWidth="3"
              strokeLinecap="round"
              fill="none"
            />
          ) : mood === 'thinking' ? (
            // Thinking - small o
            <circle cx="50" cy="48" r="4" fill="#FFE66D" />
          ) : mood === 'confused' ? (
            // Confused - wavy line
            <path
              d="M 38 48 Q 44 44 50 48 Q 56 52 62 48"
              stroke="#FF6B6B"
              strokeWidth="2"
              strokeLinecap="round"
              fill="none"
            />
          ) : (
            // Default smile
            <path
              d="M 42 47 Q 50 52 58 47"
              stroke="#4ECDC4"
              strokeWidth="2"
              strokeLinecap="round"
              fill="none"
            />
          )}
        </g>
        
        {/* TV Buttons/Details */}
        <circle cx="75" cy="55" r="3" fill="#FF6B6B" />
        <circle cx="75" cy="48" r="2" fill="#4ECDC4" />
        
        {/* Neck */}
        <rect x="42" y="63" width="16" height="8" fill="#4A5568" rx="2" />
        
        {/* Body */}
        <rect
          x="25"
          y="71"
          width="50"
          height="40"
          rx="10"
          fill="url(#bodyGradient)"
          stroke="#2D3748"
          strokeWidth="2"
        />
        
        {/* Chest Display */}
        <rect
          x="38"
          y="78"
          width="24"
          height="12"
          rx="3"
          fill="#0a0a1a"
        />
        
        {/* Heart/Status indicator */}
        <g className="animate-pulse">
          <path
            d="M 50 82 L 46 86 L 50 90 L 54 86 Z"
            fill="#FF6B6B"
          />
        </g>
        
        {/* Arms */}
        <g className={speaking ? 'animate-bounce' : ''} style={{ animationDuration: '0.5s' }}>
          {/* Left Arm */}
          <rect x="12" y="75" width="13" height="6" rx="3" fill="#4A5568" />
          <circle cx="10" cy="78" r="5" fill="#4ECDC4" />
          
          {/* Right Arm */}
          <rect x="75" y="75" width="13" height="6" rx="3" fill="#4A5568" />
          <circle cx="90" cy="78" r="5" fill="#4ECDC4" />
        </g>
        
        {/* Legs/Wheels */}
        <ellipse cx="35" cy="115" rx="10" ry="6" fill="#2D3748" />
        <ellipse cx="65" cy="115" rx="10" ry="6" fill="#2D3748" />
        <ellipse cx="35" cy="115" rx="6" ry="3" fill="#4A5568" />
        <ellipse cx="65" cy="115" rx="6" ry="3" fill="#4A5568" />
        
        {/* Gradients */}
        <defs>
          <linearGradient id="tvGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#4A5568" />
            <stop offset="100%" stopColor="#2D3748" />
          </linearGradient>
          <linearGradient id="bodyGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#4A5568" />
            <stop offset="100%" stopColor="#2D3748" />
          </linearGradient>
          <radialGradient id="screenGlow" cx="50%" cy="30%" r="50%">
            <stop offset="0%" stopColor="#4ECDC4" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#4ECDC4" stopOpacity="0" />
          </radialGradient>
        </defs>
      </svg>
    </div>
  );
};

export default OyonoRobot;
